import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import clsx from "clsx";
import moment from "moment";
import { Common, Icon, CustomScrollbar } from "@/components";
import { DeliveryItem } from "./DeliveryItem";
import {
  PATH,
  SUBSCRIPTION_PRODUCT_TYPE,
  TIME_FORMAT,
} from "@/configuration/globalVariable";

import { useAppSelector } from "@/sharedStore/hooks";
import {
  deliveryManifestLoading,
  deliveryManifestLoaded,
  optimizingGroupsSelector,
} from "../providers/deliveryManifest/selector";
import { manifest, manifestLine } from "../models/DeliveryManifest";
import { OptimizingGroup } from "./OptimizingGroup";
import { canModifyRoute } from "../utils";
import base64 from "base-64";
import { userSelector } from "@/providers/auth/selector";

interface Props {
  routesChecked: string[];
  searchKeyword: string;
  date: any;
  unallocatedDocumentCount?: number;
  onCheckRoute: (routeId: string, isStopped: boolean) => void;
  unallocateChecked: boolean;
  isETAsLoading?: boolean;
  loadingManifests: string[];
  reoptimisingManifestIds: string[];
  etaLoadingManifestIds: string[];
  onCheckUnallocate: (unallocateChecked: boolean) => void;
  manifests: manifest[];
  onChangeDriver: (manifest: manifest) => void;
  onSwap: (manifest: manifest) => void;
  onMove: (manifest: manifest) => void;
  onBasicReoptimise: (manifest: manifest) => void;
  onAdvancedReoptimise: (manifest: manifest) => void;
  onReverseSequence: (manifest: manifest) => void;
  onAddRoute: () => void;
  onMoveUnallocated: (manifestLine: manifestLine, route: manifest) => void;
  onPreviewOptimizeGroup: (optimizeGroup: any) => void;
  onCancelOptimize: () => void;
}

export const DeliveryList = ({
  isETAsLoading,
  loadingManifests,
  reoptimisingManifestIds,
  etaLoadingManifestIds,
  routesChecked,
  searchKeyword,
  date,
  unallocatedDocumentCount,
  unallocateChecked,
  onCheckUnallocate,
  onCheckRoute,
  manifests,
  onChangeDriver,
  onSwap,
  onMove,
  onBasicReoptimise,
  onAdvancedReoptimise,
  onReverseSequence,
  onAddRoute,
  onMoveUnallocated,
  onPreviewOptimizeGroup,
  onCancelOptimize,
}: Props) => {
  const navigate = useNavigate();
  const loading = useAppSelector(deliveryManifestLoading);
  const loaded = useAppSelector(deliveryManifestLoaded);
  const optimizingGroups = useAppSelector(optimizingGroupsSelector);
  const user = useAppSelector(userSelector);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [manifests, loaded]);

  useEffect(() => {
    return () => {
      ReactTooltip.hide();
    };
  }, []);

  const handleOpenAllocate = () => {
    navigate(`/${PATH.DELIVERY._}/${PATH.DELIVERY.EDIT_ROUTES}`, {
      state: {
        unallocate: true,
        deliveryDate: date,
        routesActive: [{}],
        routesUnActive: [],
      },
    });
  };

  const isRoutes = manifests && manifests.length;
  const isToday = moment().isSame(date, "day");
  const subscribedProductType = useAppSelector(
    (state) => state.authReducer.user?.subscribedProductType
  );

  const handleUploadOrders = () => {
    navigate(`/${PATH.DELIVERY._}/${PATH.DELIVERY.IMPORT}`, {
      state: {
        navigationSource: window.location.hash,
      },
    });
  };

  const handleImportIntegration = () => {
    navigate(
      `/${PATH.REDIRECT}?path=${base64.encode("/integration/documents/import")}`
    );
  };

  return (
    <>
      {/* {loaded && loading && (
        <div className="absolute top-0 left-0 h-full w-full z-50 bg-black bg-opacity-30 flex justify-center items-center rounded">
          <Icon.Loading size="28" />
        </div>
      )} */}
      {Boolean(unallocatedDocumentCount) && (
        <div className="border border-yellow bg-yellow-light flex py-2.5 px-4 items-center rounded mb-2 mt-2">
          <Common.Checkbox
            disabled={isETAsLoading}
            checked={unallocateChecked}
            onChange={(e) => onCheckUnallocate(e.target.checked)}
            ipSize="md"
          />
          <Icon.InfoFill className="ml-4 fill-yellow" />
          <span className="ml-1.5 text-sm text-yellow font-semibold">
            {unallocatedDocumentCount} unallocated orders
          </span>
          {(!optimizingGroups.length || optimizingGroups.length == 0) && (
            <Common.Button
              onClick={handleOpenAllocate}
              className="ml-3"
              size="sm"
              outline
              disabled={isETAsLoading}
            >
              Allocate now
            </Common.Button>
          )}
        </div>
      )}
      {/* No content */}
      <div
        className={clsx(
          unallocatedDocumentCount
            ? "h-[calc(100vh-22rem)]"
            : "h-[calc(100vh-18.375rem)]",
          "flex justify-center items-center h-"
        )}
      >
        {isRoutes || Boolean(optimizingGroups.length) ? (
          <CustomScrollbar>
            <div className="pr-2">
              {Boolean(optimizingGroups.length) &&
                optimizingGroups.map((optimizingGroup: any, index: number) => (
                  <OptimizingGroup
                    key={index}
                    groupIndex={index + 1}
                    isFirst={!index}
                    data={optimizingGroup}
                    onMoveUnallocated={onMoveUnallocated}
                    onPreviewOptimizeGroup={onPreviewOptimizeGroup}
                    onCancelOptimize={onCancelOptimize}
                  />
                ))}
              {manifests &&
                manifests.map((item: manifest, index: number) => (
                  <DeliveryItem
                    key={item.id}
                    isFirst={!index && !optimizingGroups.length}
                    data={item}
                    canCheck={canModifyRoute(item)}
                    isChecked={routesChecked.includes(item.id)}
                    onCheck={onCheckRoute}
                    isETAsLoading={isETAsLoading}
                    isReloading={loadingManifests.includes(item.id)}
                    isReoptimising={reoptimisingManifestIds.includes(item.id)}
                    isRouteETALoading={etaLoadingManifestIds.includes(item.id)}
                    onChangeDriver={() => onChangeDriver(item)}
                    onSwap={() => onSwap(item)}
                    onMove={() => onMove(item)}
                    onBasicReoptimise={() => onBasicReoptimise(item)}
                    onAdvancedReoptimise={() => onAdvancedReoptimise(item)}
                    onReverseSequence={() => onReverseSequence(item)}
                    isOptimisationEnabled={user.isRouteOptimisationAvailable}
                  />
                ))}
            </div>
            {/* <DeliveryItem
              onViewDetail={handleOpenDetail}
              optimized={isOptimized}
            />
            <DeliveryItem onViewDetail={handleOpenDetail} /> */}
          </CustomScrollbar>
        ) : (
          <>
            {!loading ? (
              loaded ? (
                <>
                  {searchKeyword && !manifests.length ? (
                    <Common.NoData
                      iconRender={Icon.Search}
                      iconClassName="stroke-neutral-30"
                      title="No result founded"
                      description="Please try another keyword"
                    />
                  ) : (
                    <Common.NoData
                      iconRender={Icon.Split}
                      title={
                        isToday
                          ? subscribedProductType ===
                            SUBSCRIPTION_PRODUCT_TYPE.TOMBUSINESS
                            ? `You haven't created any route yet`
                            : `No order for selected delivery date`
                          : `No data on ${moment(date).format(
                              TIME_FORMAT.GENERAL_DISPLAY
                            )}`
                      }
                      description={
                        isToday
                          ? subscribedProductType ===
                            SUBSCRIPTION_PRODUCT_TYPE.TOMBUSINESS
                            ? "Create new route to manage your orders"
                            : "Upload or import orders to start routing"
                          : ""
                      }
                      bottomElement={
                        subscribedProductType ===
                        SUBSCRIPTION_PRODUCT_TYPE.TOMBUSINESS ? (
                          <Common.Button
                            className="mt-8"
                            iconLeft={Icon.PlusBorder}
                            onClick={onAddRoute}
                          >
                            Create new delivery manifest
                          </Common.Button>
                        ) : (
                          <Common.Button
                            className="mt-8"
                            iconLeft={Icon.Upload}
                            dropdownItems={[
                              {
                                label: "Upload from file",
                                onClick: handleUploadOrders,
                              },
                              {
                                label: "Import from integration system",
                                onClick: handleImportIntegration,
                              },
                            ]}
                          >
                            Import invoices
                          </Common.Button>
                        )
                      }
                    />
                  )}
                </>
              ) : (
                <Common.NoData
                  iconRender={Icon.Warning}
                  title="Something wrong, please try again later"
                />
              )
            ) : (
              <Common.NoData
                iconRender={Icon.Loading}
                title="Loading manifests...."
              />
            )}
          </>
        )}
      </div>
    </>
  );
};
